import { createFeatureSelector, createSelector } from '@ngrx/store';

import { PackageUserRole } from 'src/app/features/package-users/models/package-user-role.enum';
import { PackageUsersSelectors } from 'src/app/features/package-users/store/selectors';
import { DeviceGroupSelectors } from 'src/app/features/device-group';
import { PackagesSelectors } from 'src/app/features/packages';
import { HelpFaqState } from '../state';

export const getHelpFaqState = createFeatureSelector<HelpFaqState.State>('helpFaq');

export const getFaqData = createSelector(
  getHelpFaqState,
  (state: HelpFaqState.State) => state.faqData
);

export const getIsFaqLoaded = createSelector(
  getHelpFaqState,
  (state: HelpFaqState.State) => state.faqData !== null
);

export const getIsFaqLoading = createSelector(
  getHelpFaqState,
  (state: HelpFaqState.State) => state.isLoading
);

export const getFaqError = createSelector(
  getHelpFaqState,
  (state: HelpFaqState.State) => state.error
);

export const getCashToCloseFromLoanEstimate = createSelector(
  getFaqData,
  (faqData) => faqData?.cashToCloseFromLoanEstimate ?? false
);

export const getCashToCloseFinalIsFromBorrower = createSelector(
  getFaqData,
  (faqData) => faqData?.cashToCloseFinalIsFromBorrower ?? false
);

export const getDisplayFundingTransfer = createSelector(
  getFaqData,
  (faqData) => faqData?.displayFundingTransfer ?? false
);

export const getPayoffs = createSelector(getFaqData, (faqData) => faqData?.payoffs ?? []);

export const getDisbursementDate = createSelector(
  getFaqData,
  (faqData) => faqData?.disbursementDate ?? null
);

export const getFirstPaymentDate = createSelector(
  getFaqData,
  (faqData) => faqData?.firstPaymentDate ?? null
);

export const getInterestRate = createSelector(
  getFaqData,
  (faqData) => faqData?.interestRate ?? null
);

export const getMonthlyPayment = createSelector(
  getFaqData,
  (faqData) => faqData?.monthlyPayment ?? null
);

export const getPrepaymentPenalty = createSelector(
  getFaqData,
  (faqData) => faqData?.prepaymentPenalty ?? null
);

// All roles that appear in the "who signs" list — matches ESign's approach (borrowers + all others).
// SYSTEM is excluded as it is an internal role, not an actual person.
const SIGNER_ROLES = new Set<string>([
  PackageUserRole.BORROWER,
  PackageUserRole.NBTH,
  PackageUserRole.SELLER,
  PackageUserRole.SIGNER,
  PackageUserRole.WITNESS,
  PackageUserRole.SIGNINGAGENT,
]);

export const getSigners = createSelector(
  PackageUsersSelectors.getPackageUsers,
  (packageUsers) => (packageUsers ?? []).filter((p) => SIGNER_ROLES.has(p.userRoleCode))
);

// True when at least one participant on the package has the NBTH role.
export const getHasNbthSigner = createSelector(
  PackageUsersSelectors.getPackageUsers,
  (packageUsers) => (packageUsers ?? []).some((p) => p.userRoleCode === PackageUserRole.NBTH)
);

// True when the package is RON and at least one non-signing-agent participant
// has authenticated (pinned in) on this device.
export const getHasAssociatedUsersAndPackage = createSelector(
  PackagesSelectors.isRON,
  DeviceGroupSelectors.hasAuthenticatedNonSAUsers,
  (isRon, hasUsers) => isRon && hasUsers
);
